import { Controller, useFormContext, type FieldPath } from 'react-hook-form';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Input, Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';
import type { ContractFormValues } from './contract-form';

type Name = FieldPath<ContractFormValues>;

interface Option {
  value: string;
  label: string;
}

/** Read a possibly-nested RHF error message by dotted path (`products.0.product_name`). */
export function errorFor(errors: unknown, path: string): string | undefined {
  let node: unknown = errors;
  for (const key of path.split('.')) {
    if (node && typeof node === 'object') node = (node as Record<string, unknown>)[key];
    else return undefined;
  }
  const msg = (node as { message?: unknown } | undefined)?.message;
  return typeof msg === 'string' ? msg : undefined;
}

/** RHF-bound single Select field built on the @ui Select family. */
export function SelectField({
  name, label, options, placeholder, required,
}: { name: Name; label: string; options: Option[]; placeholder?: string; required?: boolean }) {
  const { control, formState } = useFormContext<ContractFormValues>();
  const error = errorFor(formState.errors, name);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Field label={label} required={required} error={error}>
          {(p) => (
            <Select value={(field.value as string) || ''} onValueChange={field.onChange}>
              <SelectTrigger {...p} invalid={!!error} onBlur={field.onBlur}>
                <SelectValue placeholder={placeholder ?? `Select ${label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      )}
    />
  );
}

/** RHF-bound text/number/date Input field. */
export function InputField({
  name, label, placeholder, type = 'text', required, description,
}: {
  name: Name; label: string; placeholder?: string; type?: string;
  required?: boolean; description?: string;
}) {
  const { control, formState } = useFormContext<ContractFormValues>();
  const error = errorFor(formState.errors, name);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Field label={label} required={required} error={error} description={description}>
          {(p) =>
            type === 'date' ? (
              <DateInput
                {...p}
                placeholder={placeholder}
                invalid={!!error}
                value={field.value == null ? '' : String(field.value)}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            ) : type === 'number' ? (
              <NumericInput
                {...p}
                placeholder={placeholder}
                invalid={!!error}
                value={field.value == null ? '' : String(field.value)}
                onChange={(e) => field.onChange(Number(e.target.value))}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            ) : (
              <Input
                {...p}
                type={type}
                placeholder={placeholder}
                invalid={!!error}
                value={field.value == null ? '' : String(field.value)}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            )
          }
        </Field>
      )}
    />
  );
}

/** RHF-bound multiline Textarea field. */
export function TextareaField({
  name, label, placeholder, rows = 3,
}: { name: Name; label: string; placeholder?: string; rows?: number }) {
  const { control, formState } = useFormContext<ContractFormValues>();
  const error = errorFor(formState.errors, name);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Field label={label} error={error}>
          {(p) => (
            <Textarea
              {...p}
              rows={rows}
              placeholder={placeholder}
              invalid={!!error}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
          )}
        </Field>
      )}
    />
  );
}
